/**
 * Tools reducer
 */

import { IAction, ActionReducer } from '..';
import {
  ADD_ACTION_STATUS,
  ADD_ACTION_STATUS_ERROR,
  ADD_ACTION_STATUS_SUCCESS,
  CLICK_NAVIGATE_BUTTON,
  FETCH_API_INFO_ERROR,
  FETCH_API_INFO_SUCCESS,
  FETCH_COMPONENT_INFO_ERROR,
  FETCH_COMPONENT_INFO_SUCCESS,
  REMOVE_ALL_ACTION_STATUSES,
  ROUTER_NAVIGATION_END,
  SEND_HTTP_REQUEST_ERROR,
  SEND_HTTP_REQUEST_SUCCESS,
  UiActions,
  XprtDataActions,
} from '@store/actions';

import { get, isNil, keyBy, mapValues, reject, some } from 'lodash';
import * as baseAction from '@store/actions/base';
import { ACTION_STATUS, UI_FEATURES } from '@config/index';
import { XVIError } from '@shared/models/xvi-error.model';
import { environment } from '../../../../environments/environment';
import { getBool, removeActionPostfix } from '@shared/utils';
import { IXVIFeature } from '@config/features';
import { IActionStatusItem } from '@shared/types';

export const getFeatureValueFromEnvironment = (
  env,
  feature: IXVIFeature,
): boolean => {
  const environmentFeatureValue = String(get(env, feature.name));
  const isVariableSet =
    environmentFeatureValue !== 'undefined' && environmentFeatureValue !== '';

  return isVariableSet ? getBool(environmentFeatureValue) : feature.default;
};

// composing initial feature values
const featureDefaults: { [key: string]: boolean } = mapValues(
  keyBy(UI_FEATURES, 'name'),
  (feature) => getFeatureValueFromEnvironment(environment, feature),
);

const actionStatusConstructHelper = (
  action: IAction,
  status: ACTION_STATUS,
): IActionStatusItem => ({
  status,
  id: action.payload.requestId,
  failedAction: action.payload.actionType,
  action: removeActionPostfix(action.payload.actionType),
  actionPayload: action.payload.actionPayload,
});

const initialState: IToolsState = {
  operationResult: null,
  history: [],
  navigateTo: null,
  apiInfo: {},
  componentInfo: {},
  features: featureDefaults,
  iframePostMessageResult: {
    response: {},
    options: {
      messageId: null,
    },
    errors: [],
  },
};

export interface IToolsState {
  operationResult: object;
  history: string[];
  navigateTo: object;
  apiInfo: object;
  componentInfo: object;
  features: { [key: string]: boolean };
  iframePostMessageResult: {
    response: object;
    options: {
      messageId: string;
    };
    errors: XVIError[];
  };
}

// eslint-disable-next-line complexity
export const toolsReducer: ActionReducer<IToolsState> = (
  state = initialState,
  action: IAction,
): IToolsState => {
  const actionType: string = get(action, 'type');
  switch (actionType) {
    case XprtDataActions.EVENT_SELECTION_SUCCESS:
      return Object.assign({}, state, {
        iframePostMessageResult: {
          response: get(action, 'payload', {}),
          options: {
            messageId: get(action, 'payload.messageId', null),
          },
          errors: [],
        },
      });
    case XprtDataActions.EVENT_SELECTION_CANCEL:
      return Object.assign({}, state, {
        iframePostMessageResult: {
          response: get(action, 'payload', {}),
          options: {
            messageId: get(action, 'payload.messageId', null),
          },
          errors: [],
        },
      });

    case XprtDataActions.EVENT_SELECTION_ERROR:
      return Object.assign({}, state, {
        iframePostMessageResult: {
          response: get(action, 'payload', {}),
          options: {
            messageId: get(action, 'payload.messageId', null),
          },
          errors: [
            {
              code: get(action, 'payload.payload.code', ''),
              message: get(action, 'payload.payload.message', ''),
            },
          ],
        },
      });
    case SEND_HTTP_REQUEST_ERROR:
    case SEND_HTTP_REQUEST_SUCCESS:
    // API actions results
    // eslint-disable-next-line no-fallthrough
    case baseAction.FETCH_SCHEMA_SUCCESS:
    case baseAction.FETCH_SCHEMA_ERROR:
    case baseAction.FETCH_LIST_SUCCESS:
    case baseAction.FETCH_LIST_ERROR:
    case baseAction.FETCH_GET_DOCUMENT_BY_ID_SUCCESS:
    case baseAction.FETCH_GET_DOCUMENT_BY_ID_ERROR:
    case baseAction.FETCH_CREATE_ENTITY_SUCCESS:
    case baseAction.FETCH_CREATE_ENTITY_ERROR:
    case baseAction.FETCH_UPDATE_ENTITY_SUCCESS:
    case baseAction.FETCH_UPDATE_ENTITY_ERROR:
    case baseAction.DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS:
    case baseAction.DELETE_DOCUMENT_BY_ENTITY_ID_ERROR:
    case baseAction.BULK_UPDATE_SUCCESS:
    case baseAction.BULK_UPDATE_ERROR:
      return Object.assign({}, state, {
        operationResult: get(action, 'payload'),
      });

    case ROUTER_NAVIGATION_END:
      return Object.assign({}, state, {
        history: [action.payload.uri].concat(state.history),
        currentEntityType: action.payload.entityType,
      });
    case CLICK_NAVIGATE_BUTTON:
      return Object.assign({}, state, {
        navigateTo: Object.assign({}, action.payload.options),
      });
    case FETCH_API_INFO_SUCCESS:
    case FETCH_API_INFO_ERROR:
      const operationResult = get(action, 'payload');
      if (isNil(operationResult?.response)) {
        operationResult.response = {};
      }

      return Object.assign({}, state, {
        operationResult,
        apiInfo: get(action, 'payload.response', {}),
      });
    case FETCH_COMPONENT_INFO_ERROR:
    case FETCH_COMPONENT_INFO_SUCCESS:
      return Object.assign({}, state, {
        componentInfo: get(action, 'payload'),
      });

    // features which are not listed in the action.payload will not be affected
    case UiActions.SET_UI_FEATURES:
      return Object.assign({}, state, {
        features: Object.assign({}, state.features, action.payload),
      });

    case ADD_ACTION_STATUS:
      return Object.assign({}, state, {
        actionStatuses: [
          ...reject(get(state, 'actionStatuses', []), {
            id: action.payload.requestId,
          }),
          actionStatusConstructHelper(action, ACTION_STATUS.IN_PROGRESS),
        ],
      });
    case ADD_ACTION_STATUS_ERROR:
      if (
        !some(get(state, 'actionStatuses', []), {
          id: action.payload.requestId,
        })
      ) {
        return state;
      }

      return Object.assign({}, state, {
        actionStatuses: [
          ...reject(get(state, 'actionStatuses', []), {
            id: action.payload.requestId,
          }),
          actionStatusConstructHelper(action, ACTION_STATUS.ERROR),
        ],
      });
    case ADD_ACTION_STATUS_SUCCESS:
      return Object.assign({}, state, {
        actionStatuses: [
          ...reject(get(state, 'actionStatuses', []), {
            id: action.payload.requestId,
          }),
          actionStatusConstructHelper(action, ACTION_STATUS.SUCCESS),
        ],
      });
    case REMOVE_ALL_ACTION_STATUSES:
      return Object.assign({}, state, {
        actionStatuses: [],
      });
    default:
      return state;
  }
};
